"use client";

import { Analytics } from "@vercel/analytics/next";
import Link from "next/link";
import { useState, useSyncExternalStore } from "react";

import {
  buildLandingAnalyticsConsentCookie,
  buildLandingAnalyticsConsentHostDeletionCookie,
  clearLandingProductAnalyticsIdentity,
  landingAnalyticsConsentState,
  type LandingAnalyticsConsentState,
} from "@/lib/landingProductAnalytics";

type ConsentChoice = Exclude<LandingAnalyticsConsentState, "unset">;

function subscribeToConsentChange(onChange: () => void): () => void {
  window.addEventListener("dronejob:analytics-consent-changed", onChange);
  return () => window.removeEventListener("dronejob:analytics-consent-changed", onChange);
}

function readConsent(): LandingAnalyticsConsentState {
  return landingAnalyticsConsentState(document.cookie);
}

export function LandingAnalyticsConsentControls({
  analyticsAvailable,
  vercelAnalyticsAvailable,
}: Readonly<{
  analyticsAvailable: boolean;
  vercelAnalyticsAvailable: boolean;
}>) {
  const consent = useSyncExternalStore(
    subscribeToConsentChange,
    readConsent,
    () => "loading" as const,
  );
  const [preferencesOpen, setPreferencesOpen] = useState(false);

  if (!analyticsAvailable) return null;

  const choose = (choice: ConsentChoice) => {
    const secure = window.location.protocol === "https:";
    // Remove the legacy host-only cookie before writing the parent-domain
    // choice used by both the public and authenticated DroneJob surfaces.
    document.cookie = buildLandingAnalyticsConsentHostDeletionCookie(secure);
    document.cookie = buildLandingAnalyticsConsentCookie(
      choice,
      secure,
      window.location.hostname,
    );
    if (choice === "denied") {
      clearLandingProductAnalyticsIdentity(window.localStorage);
    }
    setPreferencesOpen(false);
    window.dispatchEvent(new Event("dronejob:analytics-consent-changed"));
  };
  const showPanel = consent === "unset" || preferencesOpen;

  return (
    <>
      {vercelAnalyticsAvailable && consent === "granted" ? <Analytics /> : null}
      {consent !== "loading" && !showPanel ? (
        <button
          type="button"
          className="fixed bottom-4 left-4 z-[80] rounded-full border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-800 shadow-lg hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-offset-2"
          onClick={() => setPreferencesOpen(true)}
        >
          Privacy choices
        </button>
      ) : null}
      {consent !== "loading" && showPanel ? (
        <section
          aria-label="Analytics privacy choices"
          aria-live="polite"
          className="fixed inset-x-4 bottom-4 z-[90] mx-auto max-w-3xl rounded-2xl border border-slate-200 bg-white p-5 text-slate-900 shadow-2xl sm:p-6"
        >
          <h2 className="text-lg font-semibold">Your analytics choice</h2>
          <p className="mt-2 text-sm leading-6 text-slate-700">
            Optional analytics help DroneJob understand which public pages are useful.
            They stay off unless you accept. We use an anonymous browser identifier and
            do not connect it to your signed-in account.
          </p>
          <p className="mt-2 text-sm text-slate-600">
            Read the <Link className="underline hover:text-slate-900" href="/cookie-policy">Cookie Policy</Link>
            {" and "}
            <Link className="underline hover:text-slate-900" href="/privacy">Privacy Policy</Link>.
          </p>
          <div className="mt-5 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
            <button
              type="button"
              className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-offset-2"
              onClick={() => choose("denied")}
            >
              {consent === "granted" ? "Withdraw optional analytics" : "Reject optional analytics"}
            </button>
            <button
              type="button"
              className="rounded-lg bg-blue-700 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-offset-2"
              onClick={() => choose("granted")}
            >
              Accept optional analytics
            </button>
          </div>
        </section>
      ) : null}
    </>
  );
}
